import classnames from "classnames";

export const typesenseClientConfig = {
  query_by: "*",
}

export const Config = {
  Defaults: {
    TimeIntervals: 8
  },
  Styles: {
    Chart: {
      DefaultLineStyle: 'linear', // monotone || linear
      Axis: {
        Tick: {fill: '#000000', fontSize: 13},
        AxisLine: {stroke: '#000000', width: 2}
      },
      Width: '100%',
      Height: 600,
    },
  },
  I18n: {
    Filters: {
      Title: 'Filters'
    },
    Timeline: {
      DefaultValue: 'All'
    },
    Hits: {
      NoResults: 'No data for the current selection.'
    },
    Table: {
      NoData: 'Data is being prepared. Please try again shortly.'
    }
  }
};

// Localize a chart-chrome string via the app's global i18n-js instance (locale
// from <html lang>); the English literal is the fallback when the key or the
// i18n bundle is missing. Keys live under the `nv_chart` namespace.
export function uiText(key, fallback) {
  const i18n = typeof window !== 'undefined' ? window.I18n : null;
  if (!i18n?.t) return fallback;
  try {
    return i18n.t(`nv_chart.${key}`, { defaultValue: fallback });
  } catch {
    return fallback;
  }
}

export const futureFlags = () => {
  return {
    preserveSharedStateOnUnmount: true,
  };
};

export const filtersConfig = (config) => {
  const {
    filterBy = ''
  } = config;

  if (filterBy && filterBy.trim().length > 0) {
    return `${filterBy.trim()}`
  }

  return null;
};

// The selectable Y axes for a group: the required primary `axes.y` first (the
// default), then any extra metrics from `axes.yOptions` ([{ label, field }, ...])
// de-duplicated by field. So the config never repeats the primary metric — the
// switcher merges them. Single-Y charts (no yOptions) stay one entry, no switcher.
export function yAxesOf(config) {
  const axes = config?.axes;
  const list = [];
  if (axes?.y?.field) list.push(axes.y);
  if (Array.isArray(axes?.yOptions)) {
    axes.yOptions.forEach(option => {
      if (option?.field && !list.some(a => a.field === option.field)) list.push(option);
    });
  }
  return list;
}

// A shallow copy of the group config with a specific Y axis selected, so the
// downstream buildData/LineChartWidget project the chosen metric and label.
// Returns the config untouched when no axis is supplied.
export function withYAxis(config, yAxis) {
  if (!yAxis) return config;
  return { ...config, axes: { ...config.axes, y: yAxis } };
}

// A shallow copy of the group config with the X axis field (and optionally its
// label) overridden — used by the axis switcher to project a chosen field onto
// the X axis. Untouched when no field is given.
export function withXAxis(config, field, label) {
  if (!field) return config;
  const x = { ...config.axes?.x, field };
  if (label) x.label = label;
  return { ...config, axes: { ...config.axes, x } };
}

// The metric base of a percentile field: "p90_intvty" -> "intvty",
// "p99_9_ttft" -> "ttft". Returns null for a non-percentile field.
const PERCENTILE_FIELD = /^p\d+(?:_\d+)?_(.+)$/;
export function percentileBase(field) {
  const match = PERCENTILE_FIELD.exec(String(field ?? ''));
  return match ? match[1] : null;
}

// Numeric value of a percentile token: "p90" -> 90, "p99_9" -> 99.9.
function percentileValue(token) {
  return parseFloat(String(token).slice(1).replace('_', '.'));
}

function ordinalSuffix(n) {
  const teen = n % 100;
  if (teen >= 11 && teen <= 13) return 'th';
  return { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th';
}

// "p90" -> "90th percentile", "p99_9" -> "99.9th percentile".
export function percentileLabel(token) {
  const n = percentileValue(token);
  return `${n}${Number.isInteger(n) ? ordinalSuffix(n) : 'th'} percentile`;
}

// Per-row percentile fallback (§1.5): ensure every row has a value under
// `field` (the selected percentile) by copying the nearest populated
// `p<N>_<base>` the row actually carries. Rows with no percentile at all are
// left untouched (their point drops). Returns a new array; inputs aren't
// mutated. No-op when no base/field is given, or when `percentile` is null —
// i.e. the selected axis field isn't a percentile, so there's nothing to
// approximate (guarding this avoids treating null as 0 and copying the lowest
// percentile into an unrelated field).
export function withPercentileFallback(rows, base, percentile, field) {
  if (!base || !field || percentile == null || !Array.isArray(rows)) return rows;
  const re = new RegExp(`^p(\\d+(?:_\\d+)?)_${base}$`);
  return rows.map(row => {
    if (!row) return row;
    let best = null;
    let bestDistance = Infinity;
    Object.keys(row).forEach(key => {
      const match = re.exec(key);
      if (match && row[key] != null) {
        const distance = Math.abs(parseFloat(match[1].replace('_', '.')) - percentile);
        if (distance < bestDistance) { bestDistance = distance; best = row[key]; }
      }
    });
    return best == null ? row : { ...row, [field]: best };
  });
}

// Sort key for a slash-delimited numeric pair label ("128K/8K" -> first part
// primary, second secondary), so grouped values order numerically small -> large.
// Handles k/m/g magnitude suffixes; non-numeric tokens sort as 0.
function numericPairSortKey(seq) {
  const parts = String(seq).split('/').map(token => {
    const m = /([\d.]+)\s*([kmg]?)/i.exec(token.trim());
    if (!m) return 0;
    const mult = { k: 1e3, m: 1e6, g: 1e9 }[m[2].toLowerCase()] || 1;
    return parseFloat(m[1]) * mult;
  });
  return (parts[0] || 0) * 1e15 + (parts[1] || 0);
}

function titleCase(value) {
  return String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Options for a grouped dropdown, derived from `rows`. Each entry in `sections`
// maps a data field to a named group in the dropdown; sections appear in the
// order listed. `valueLabels` maps raw field values to display labels.
// Each option: { key:'category:value_a', field, value, label, group }.
export function groupedOptionsOf(rows, { sections, valueLabels } = {}) {
  if (!Array.isArray(rows)) return [];
  // Normalize rather than rely on parameter defaults: those only fire for
  // `undefined`, and the JSON config fields can legitimately hold `null`
  // (typed into the JSON editor, or arriving from an imported config).
  const groups = Array.isArray(sections) ? sections : [];
  const labels = valueLabels || {};
  const result = [];
  groups.forEach(section => {
    if (!section?.field || !section?.label) return;
    const seen = new Set();
    const values = [];
    rows.forEach(row => {
      if (!row) return;
      const v = row[section.field];
      if (v != null && v !== '' && !seen.has(v)) { seen.add(v); values.push(v); }
    });
    values
      .sort((a, b) => {
        const ka = numericPairSortKey(a), kb = numericPairSortKey(b);
        return (ka || kb) ? ka - kb : String(a).localeCompare(String(b));
      })
      .forEach(value => {
        result.push({
          key: `${section.field}:${value}`,
          field: section.field,
          value,
          label: labels[value] || titleCase(value),
          group: section.label,
        });
      });
  });
  return result;
}

// Fields normalized to uppercase for display (README §4/§2.4): producers emit
// mixed casing (v3 `fp8` vs VPR `FP4`); the v4 codec uppercases on write, so we
// mirror that on read for rows that skipped the codec, so users never see the
// same value in two casings. Returns the row untouched when nothing changes.
const UPPERCASE_FIELDS = ['precision', 'kv_cache_precision'];
export function normalizeCasing(row) {
  if (!row) return row;
  let out = row;
  UPPERCASE_FIELDS.forEach(field => {
    const value = row[field];
    if (typeof value === 'string' && value && value !== value.toUpperCase()) {
      if (out === row) out = { ...row };
      out[field] = value.toUpperCase();
    }
  });
  return out;
}

// react-search-ui wraps each result field as { raw }. Flatten to plain rows.
export function normalizeSearchResults(results, wasSearched) {
  if (!wasSearched || !Array.isArray(results) || results.length === 0) return [];
  return results.map(entry => {
    const row = {};
    Object.entries(entry).forEach(([key, val]) => {
      if (val && typeof val === 'object' && 'raw' in val) row[key] = val.raw;
    });
    return normalizeCasing(row);
  });
}

export function chartStyles(style) {
  return {
    root: classnames({
      'nv-chart': true,
      [`nv-chart--style-${style}`]: true
    })
  };
}

export function templateReplace(str, data) {
  return str.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
    return data[key] !== undefined ? data[key] : '';
  });
}

// Pareto frontier over a group's points, both axes maximised (pareto §7). Returns
// a Set of the point objects that lie on the frontier; the rest are dominated.
// Ties on X keep only the max-Y candidate. A point is on the frontier iff its Y is
// strictly greater than the max Y among all points with strictly higher X.
function paretoMask(points, getX, getY) {
  const onFrontier = new Set();
  const ordered = points
    .map(p => ({ p, x: getX(p), y: getY(p) }))
    .filter(o => Number.isFinite(o.x) && Number.isFinite(o.y))
    .sort((a, b) => b.x - a.x || b.y - a.y);
  let bestY = -Infinity;
  let i = 0;
  while (i < ordered.length) {
    const cand = ordered[i]; // max-Y at this X — list is Y-desc within equal X
    const x = cand.x;
    let j = i;
    while (j < ordered.length && ordered[j].x === x) j += 1;
    if (cand.y > bestY) { onFrontier.add(cand.p); bestY = cand.y; }
    i = j;
  }
  return onFrontier;
}

// `frontier` (opt-in; the caller passes it from config `pareto.frontierOnly`):
// draw only the Pareto-frontier points of each group as the connected line;
// dominated points go under `<id>__dom` keys so LineChartWidget can render them
// as standalone markers. Off → every point stays on the line (connectNulls), the
// original behaviour for non-Pareto charts.
export function buildData(items, config, { frontier = false } = {}) {
  const groupByFields = config.groupByFields.split(',').map(f => f.trim());
  const groupLabelFormat = config.groupLabelFormat;
  const xField = config.axes.x.field;
  const yField = config.axes.y.field;

  if (!items.length) return { data: [], groups: [] };

  // Copy rather than mutate: `items` is the caller's memoized row array (and the
  // same objects ResultsBridge publishes for the Table view / CSV), so rounding
  // in place would round the tooltip and table values too — and only on the
  // paths that happen to render first.
  items = items.map(item => ({
    ...item,
    [xField]: Math.round(item[xField]),
    [yField]: Math.round(item[yField]),
  }));

  const groupsMap = new Map();
  items.forEach(item => {
    const key = groupByFields.map(f => item[f]).join('|');
    if (!groupsMap.has(key)) {
      groupsMap.set(key, []);
    }
    groupsMap.get(key).push(item);
  });

  const frontierByGroup = new Map();
  if (frontier) {
    groupsMap.forEach((grpItems, key) => {
      frontierByGroup.set(key, paretoMask(grpItems, i => i[xField], i => i[yField]));
    });
  }

  const xValues = Array.from(new Set(items.map(i => i[xField]))).sort((a, b) => a - b);

  const maxYof = (rows) => rows.reduce((best, r) => (best && best[yField] >= r[yField] ? best : r), null);

  const data = xValues.map(xVal => {
    const obj = { [xField]: xVal };
    groupsMap.forEach((grpItems, key) => {
      if (!frontier) {
        const item = grpItems.find(i => i[xField] === xVal);
        obj[key] = item ? item[yField] : null;
        if (item) obj[`${key}__entry`] = item;
        return;
      }
      // Split this group's points at this X into frontier (the line) and dominated
      // (markers). At most one of each per X — ties collapse to the max Y.
      const onF = frontierByGroup.get(key);
      const here = grpItems.filter(i => i[xField] === xVal);
      const fBest = maxYof(here.filter(i => onF.has(i)));
      const dBest = maxYof(here.filter(i => !onF.has(i)));
      obj[key] = fBest ? fBest[yField] : null;
      if (fBest) obj[`${key}__entry`] = fBest;
      obj[`${key}__dom`] = dBest ? dBest[yField] : null;
      if (dBest) obj[`${key}__dom__entry`] = dBest;
    });
    return obj;
  });

  const groups = Array.from(groupsMap.keys()).map(key => {
    const fields = key.split('|');
    let label = key;
    if (groupLabelFormat) {
      label = groupLabelFormat;
      groupByFields.forEach((f, idx) => {
        const value = fields[idx];
        label = label.replace(`{{${f}}}`, value);
      });
    }

    return {
      id: key,
      label,
      fields,
      items: groupsMap.get(key),
    };
  });
  return { data, groups };
}

// Band-lite (README §1.5, `axes.x.band.mode:"lines"`): draw two percentile
// edges per group as separate lines on a shared numeric x grid. The lo edge
// keeps the group id (solid, group color); the hi edge gets a `__band` suffix
// (dashed, same color) and `band:'hi'` so LineChartWidget can style it. X is the
// interactivity value from each edge's percentile field, Y is the shared metric.
// Mirrors buildData's group/label logic but emits two series per group.
export function buildBandData(items, config, edges) {
  const groupByFields = config.groupByFields.split(',').map(f => f.trim());
  const groupLabelFormat = config.groupLabelFormat;
  const xField = config.axes.x.field;
  const yField = config.axes.y.field;
  if (!items.length || !Array.isArray(edges) || edges.length === 0) return { data: [], groups: [] };

  const series = new Map();
  items.forEach(item => {
    const baseKey = groupByFields.map(f => item[f]).join('|');
    edges.forEach(edge => {
      const x = item[edge.field];
      const y = item[yField];
      if (x == null || y == null) return;
      const id = edge.band === 'hi' ? `${baseKey}__band` : baseKey;
      if (!series.has(id)) series.set(id, { baseKey, band: edge.band, field: edge.field, labelSuffix: edge.labelSuffix || '', points: [] });
      series.get(id).points.push({ x: Math.round(x), y: Math.round(y), entry: item });
    });
  });

  const xValues = Array.from(new Set(
    Array.from(series.values()).flatMap(s => s.points.map(p => p.x))
  )).sort((a, b) => a - b);

  const data = xValues.map(xVal => {
    const obj = { [xField]: xVal };
    series.forEach((s, id) => {
      const point = s.points.find(p => p.x === xVal);
      obj[id] = point ? point.y : null;
      if (point) obj[`${id}__entry`] = point.entry;
    });
    return obj;
  });

  const groups = Array.from(series.entries()).map(([id, s]) => {
    const fields = s.baseKey.split('|');
    let label = s.baseKey;
    if (groupLabelFormat) {
      label = groupLabelFormat;
      groupByFields.forEach((f, idx) => { label = label.replace(`{{${f}}}`, fields[idx]); });
    }
    // xField = the edge's own field (e.g. p90_intvty), so the tooltip can show
    // this edge's value/percentile instead of the shared configured X field.
    return { id, label: `${label}${s.labelSuffix}`, fields, band: s.band, xField: s.field, items: s.points.map(p => p.entry) };
  });

  return { data, groups };
}

// Per-group polygon geometry for the shaded band (README §1.5,
// `axes.x.band.mode:"area"`): the lo and hi percentile curves in data coords
// (x = interactivity, y = metric), each sorted by x. LineChartWidget maps these
// through the axis scales in a <Customized> layer to fill the region between the
// two curves. Keyed by the group id (matches buildBandData's solid edge, so the
// colours resolve identically). No interpolation — the polygon uses the real
// points, so on non-monotone (non-Pareto) data it can look uneven (README note).
export function bandAreaGroupsOf(items, config, loField, hiField) {
  const groupByFields = config.groupByFields.split(',').map(f => f.trim());
  const yField = config.axes.y.field;
  const map = new Map();
  (items || []).forEach(item => {
    if (!item) return;
    const lo = item[loField];
    const hi = item[hiField];
    const y = item[yField];
    if (lo == null || hi == null || y == null) return;
    const id = groupByFields.map(f => item[f]).join('|');
    if (!map.has(id)) map.set(id, { id, lo: [], hi: [] });
    const g = map.get(id);
    g.lo.push({ x: Number(lo), y: Number(y) });
    g.hi.push({ x: Number(hi), y: Number(y) });
  });
  return Array.from(map.values()).map(g => ({
    id: g.id,
    lo: g.lo.slice().sort((a, b) => a.x - b.x),
    hi: g.hi.slice().sort((a, b) => a.x - b.x),
  }));
}

export function IsRefinableItem(list, item){
  return item.count > 0 && list.includes(item.value);
}
